"use client";

import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { IoChevronDown } from "react-icons/io5";
import { TbScubaMask, TbScubaDivingTank, TbScubaDivingTankFilled } from "react-icons/tb";
import { GiIsland, GiSwimfins, GiDivingHelmet, GiTurtle, GiMantaRay, GiScubaTanks } from "react-icons/gi";
import { LuWaves } from "react-icons/lu";
import { FaRegCompass } from "react-icons/fa";
import { MdOutlineDirectionsBoatFilled, MdScubaDiving } from "react-icons/md";
import { IconType } from "react-icons";
import { getCategoryDisplayLabel } from "@/app/libs/categoryDisplayLabel";

const DIVE_COURSES: Array<{ label: string; icon: IconType }> = [
  { label: "Intro", icon: GiDivingHelmet },
  { label: "Open Water", icon: LuWaves },
  { label: "Advanced OW", icon: FaRegCompass },
  { label: "Rescue", icon: MdOutlineDirectionsBoatFilled },
  { label: "Divemaster", icon: GiTurtle },
  { label: "Instructor", icon: GiMantaRay },
  { label: "Nitrox", icon: TbScubaDivingTank },
  { label: "Deep", icon: MdScubaDiving },
  { label: "Other Specialties", icon: GiScubaTanks },
  { label: "Packs", icon: TbScubaDivingTankFilled },
];

const TOP_VERTICALS: Array<{
  label: string;
  href: string;
  icon: IconType;
  isActive: (pathname: string | null, category: string | null) => boolean;
}> = [
  {
    label: "Snorkel",
    href: "/search?category=Snorkel",
    icon: TbScubaMask,
    isActive: (pathname, category) =>
      (pathname === "/" || pathname === "/search") && category === "Snorkel",
  },
  {
    label: "Fun Dives",
    href: "/search?category=FUN DIVES",
    icon: GiIsland,
    isActive: (pathname, category) =>
      (pathname === "/" || pathname === "/search") && category === "FUN DIVES",
  },
  {
    label: "Liveaboards",
    href: "/liveaboards",
    icon: MdOutlineDirectionsBoatFilled,
    isActive: (pathname) => !!pathname?.startsWith("/liveaboards"),
  },
  {
    label: "Free Diving",
    href: "/search?category=Freediving",
    icon: GiSwimfins,
    isActive: (pathname, category) =>
      (pathname === "/" || pathname === "/search") && category === "Freediving",
  },
];

export default function VerticalNav() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const category = searchParams?.get("category") ?? null;
  const [coursesOpen, setCoursesOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const closeMenu = useCallback(() => setCoursesOpen(false), []);

  useEffect(() => {
    if (!coursesOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        closeMenu();
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [coursesOpen, closeMenu]);

  const topBox =
    "shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2 border-b-2 transition whitespace-nowrap";
  const inactive = "border-transparent text-cyan-700 hover:text-cyan-900";
  const active = "border-cyan-700 text-cyan-900";

  const searchWithCategory = (label: string) => {
    const q = new URLSearchParams(pathname?.startsWith("/gifts") ? "" : searchParams?.toString() ?? "");
    q.set("category", label);
    q.delete("page");
    // Coming from /liveaboards the date filter is named departureAfter,
    // which /search does not read — carry it over as date.
    const departureAfter = q.get("departureAfter");
    if (departureAfter && !q.get("date")) q.set("date", departureAfter);
    q.delete("departureAfter");
    return `/search?${q.toString()}`;
  };

  const searchVerticalHref = searchWithCategory;

  // Carry the current search filters (date, location, price...) into the
  // Liveaboards tab instead of dropping them with a bare href.
  const liveaboardsHref = () => {
    const q = new URLSearchParams(pathname?.startsWith("/gifts") ? "" : searchParams?.toString() ?? "");
    q.delete("category");
    q.delete("page");
    const qs = q.toString();
    return qs ? `/liveaboards?${qs}` : "/liveaboards";
  };

  const diveCoursesActive =
    (pathname === "/" || pathname === "/search") &&
    !!category &&
    DIVE_COURSES.some((c) => c.label === category);

  return (
    <div
      className="mx-auto w-full max-w-[1400px] px-3 sm:px-4 md:px-6 lg:px-8 bg-white"
      ref={menuRef}
    >
      <div className="categories-horizontal-scroll overflow-x-auto overscroll-x-contain pt-2 pb-1 md:pb-0">
        <nav
          className="flex w-max min-w-full flex-nowrap flex-row items-center justify-center gap-4 md:gap-6 mx-auto"
          aria-label="Explore"
        >
          {TOP_VERTICALS.slice(0, 1).map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.label}
                href={
                  item.href.startsWith("/search?category=")
                    ? searchVerticalHref(
                        decodeURIComponent(item.href.split("category=")[1] ?? "")
                      )
                    : item.href === "/liveaboards"
                      ? liveaboardsHref()
                      : item.href
                }
                className={`${topBox} ${
                  item.isActive(pathname, category) ? active : inactive
                }`}
              >
                <Icon className="h-5 w-5" />
                <span className="font-semibold text-sm leading-tight">
                  {item.label}
                </span>
              </Link>
            );
          })}

          <div className="relative">
            <button
              type="button"
              aria-expanded={coursesOpen}
              aria-haspopup="menu"
              onClick={() => setCoursesOpen((o) => !o)}
              className={`${topBox} ${diveCoursesActive ? active : inactive}`}
            >
              <TbScubaDivingTank className="h-5 w-5" />
              <span className="font-semibold text-sm leading-tight inline-flex items-center gap-1">
                Dive Courses
                <IoChevronDown
                  className={`w-4 h-4 transition ${
                    coursesOpen ? "rotate-180" : ""
                  }`}
                />
              </span>
            </button>
          </div>

          {TOP_VERTICALS.slice(1).map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.label}
                href={
                  item.href.startsWith("/search?category=")
                    ? searchVerticalHref(
                        decodeURIComponent(item.href.split("category=")[1] ?? "")
                      )
                    : item.href === "/liveaboards"
                      ? liveaboardsHref()
                      : item.href
                }
                className={`${topBox} ${
                  item.isActive(pathname, category) ? active : inactive
                }`}
              >
                <Icon className="h-5 w-5" />
                <span className="font-semibold text-sm leading-tight">
                  {item.label}
                </span>
              </Link>
            );
          })}
        </nav>
      </div>

      {coursesOpen ? (
        <div className="border-t border-gray-100 bg-white pt-1 pb-1 md:pb-0" role="menu">
          <div className="overflow-x-auto overscroll-x-contain">
            <div className="inline-flex min-w-max flex-nowrap flex-row items-center justify-start gap-4 md:gap-6 px-1">
              {DIVE_COURSES.map((course) => {
                const CIcon = course.icon;
                const selected = category === course.label;
                return (
                  <Link
                    key={course.label}
                    href={searchWithCategory(course.label)}
                    role="menuitem"
                    onClick={closeMenu}
                    className={`shrink-0 flex flex-col items-center justify-center gap-1 px-3 py-1.5 border-b-2 transition min-w-[92px] ${
                      selected
                        ? "border-b-neutral-800 text-neutral-800"
                        : "border-transparent text-neutral-500 hover:text-neutral-800"
                    }`}
                  >
                    <CIcon className="h-5 w-5" />
                    <span className="font-medium text-[11px] md:text-sm text-center leading-tight">
                      {getCategoryDisplayLabel(course.label)}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
